import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, fmtMoney, fmtPct, API } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import BadgeGrid from "@/components/BadgeGrid";
import EmojiPicker from "@/components/EmojiPicker";
import { ArrowLeft, Copy, UserPlus, UserMinus, Trophy, Robot, Share, PencilSimple, Check, X } from "@phosphor-icons/react";
import { toast } from "sonner";

const STRATEGY_LABEL = {
  ma_crossover: "MA Crossover",
  rsi: "RSI",
  macd: "MACD",
  bollinger: "Bollinger",
};

export default function PublicTraderPage() {
  const { tag } = useParams();
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [loading, setLoading] = useState(true);
  const [following, setFollowing] = useState(false);
  const [self, setSelf] = useState(false);
  const [followBusy, setFollowBusy] = useState(false);
  const [badges, setBadges] = useState([]);
  const [editingBio, setEditingBio] = useState(false);
  const [bioDraft, setBioDraft] = useState("");
  const [bioSaving, setBioSaving] = useState(false);
  const bioInputRef = useRef(null);

  useEffect(() => {
    api
      .get(`/leaderboard/trader/${tag}`)
      .then((r) => {
        setProfile(r.data);
        setBioDraft(r.data.bio || "");
      })
      .catch((e) => {
        if (e.response?.status === 404) setNotFound(true);
      })
      .finally(() => setLoading(false));
    // Public badges load in parallel and don't block the main render
    api.get(`/leaderboard/trader/${tag}/badges`)
      .then((r) => setBadges(r.data.badges || []))
      .catch(() => {});
  }, [tag]);

  // Derive `self` locally from the tag suffix so it works even when the user
  // has zero public bots (which used to hide the bio-add UI for fresh users).
  useEffect(() => {
    if (!user || !tag) return;
    const suffix = tag.startsWith("trader_") ? tag.slice("trader_".length) : "";
    const uidSuffix = (user.id || "").slice(-suffix.length);
    setSelf(suffix.length >= 4 && suffix.toLowerCase() === uidSuffix.toLowerCase());
  }, [user, tag]);

  // Follow status only hydrates `following` — needs any bot the trader owns.
  useEffect(() => {
    if (!user || !profile?.bots?.length) return;
    api.get(`/follows/status/${profile.bots[0].bot_id}`).then((r) => {
      setFollowing(!!r.data.following);
    }).catch(() => {});
  }, [user, profile]);

  // Live fanout: subscribe to the public trader-tag SSE channel so any new bot
  // the trader deploys is prepended to the table without a page refresh.
  useEffect(() => {
    if (!tag) return;
    let es;
    try {
      es = new EventSource(`${API}/events/trader/${encodeURIComponent(tag)}`);
      es.addEventListener("trader_new_bot", (ev) => {
        try {
          const row = JSON.parse(ev.data);
          setProfile((prev) => {
            if (!prev) return prev;
            // Guard against duplicate events (e.g. reconnect replay)
            if (prev.bots?.some((b) => b.bot_id === row.bot_id)) return prev;
            const newRow = {
              bot_id: row.bot_id,
              symbol: row.symbol,
              strategy: row.strategy,
              created_at: row.created_at || row.at,
              num_trades: 0,
              num_sells: 0,
              wins: 0,
              losses: 0,
              win_rate: 0,
              total_realized_pl: 0,
              share_count: 0,
              tags: [],
              _live: true,  // flag so we can highlight the row briefly
            };
            return {
              ...prev,
              num_bots: (prev.num_bots || 0) + 1,
              bots: [newRow, ...(prev.bots || [])],
            };
          });
          toast.success(`New bot: ${row.symbol} ${row.strategy}`, { duration: 4000 });
          // Fade the "live" flag after 5s so the highlight is subtle
          setTimeout(() => {
            setProfile((prev) => {
              if (!prev) return prev;
              return {
                ...prev,
                bots: prev.bots.map((b) => b.bot_id === row.bot_id ? { ...b, _live: false } : b),
              };
            });
          }, 5000);
        } catch {}
      });
    } catch {}
    return () => { try { es?.close(); } catch {} };
  }, [tag]);

  const toggleFollow = async () => {
    if (!user) { toast.info("Sign in to follow traders"); return; }
    if (followBusy || self) return;
    const anyBotId = profile?.bots?.[0]?.bot_id;
    if (!anyBotId) {
      toast.error("This trader has no public bots to follow through");
      return;
    }
    setFollowBusy(true);
    try {
      if (following) {
        await api.delete(`/follows/${anyBotId}`);
        setFollowing(false);
        toast.success(`Unfollowed ${tag}`);
      } else {
        await api.post(`/follows/tag/${tag}`);
        setFollowing(true);
        toast.success(`Now following ${tag}`);
      }
    } catch {
      toast.error("Could not update follow — please retry");
    } finally {
      setFollowBusy(false);
    }
  };

  const saveBio = async () => {
    if (bioSaving) return;
    setBioSaving(true);
    try {
      const r = await api.put("/me/bio", { bio: bioDraft.slice(0, 60) });
      setProfile({ ...profile, bio: r.data.bio });
      setEditingBio(false);
      toast.success("Bio updated");
    } catch {
      toast.error("Could not save bio");
    } finally {
      setBioSaving(false);
    }
  };

  const shareProfile = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({ title: `STRATOS — ${tag}`, url });
        toast.success("Shared");
        return;
      }
    } catch {}
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Profile link copied to clipboard");
    } catch {
      toast.error("Could not copy link");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center mono text-xs text-zinc-500">
        Loading trader…
      </div>
    );
  }

  if (notFound || !profile) {
    return (
      <div className="min-h-screen bg-black text-white flex flex-col items-center justify-center gap-4">
        <div className="mono text-[10px] text-zinc-500 uppercase tracking-widest">404</div>
        <div className="heading text-3xl font-black tracking-tighter">Trader not found</div>
        <Link
          to="/leaderboard"
          className="mono text-[10px] uppercase tracking-widest text-zinc-400 hover:text-white border border-zinc-800 rounded-sm px-4 py-2"
        >
          ← Browse the leaderboard
        </Link>
      </div>
    );
  }

  const positive = profile.total_realized_pl >= 0;

  return (
    <div className="min-h-screen bg-black text-white" data-testid="public-trader-page">
      {/* Top bar */}
      <header className="border-b border-zinc-800">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/leaderboard" className="mono text-[10px] uppercase tracking-widest text-zinc-500 hover:text-white flex items-center gap-2">
            <ArrowLeft size={12} /> Leaderboard
          </Link>
          <Link to="/" className="flex items-center gap-2">
            <div className="w-6 h-6 flex items-center justify-center bg-[#00C805] rounded-sm">
              <span className="text-black font-black text-xs">S</span>
            </div>
            <span className="heading font-black tracking-tighter">STRATOS</span>
          </Link>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10 space-y-8">
        {/* Hero */}
        <div className="flex items-start justify-between flex-wrap gap-6">
          <div>
            <div className="mono text-[10px] text-zinc-500 uppercase tracking-widest flex items-center gap-2">
              <Trophy size={12} className="text-[#00C805]" /> Trader profile · anonymized
            </div>
            <h1 className="heading text-5xl font-black tracking-tighter mt-3">{profile.owner_tag}</h1>
            <div className="mono text-xs text-zinc-500 uppercase tracking-widest mt-2">
              {profile.num_bots} bot{profile.num_bots === 1 ? "" : "s"} · {profile.num_trades} trades ·
              {" "}{profile.share_count} share{profile.share_count === 1 ? "" : "s"}
            </div>
            {/* Bio line — read-only for visitors, editable inline for owner */}
            {editingBio ? (
              <div className="mt-3 flex items-center gap-2" data-testid="trader-bio-editor">
                <input
                  ref={bioInputRef}
                  type="text"
                  maxLength={60}
                  value={bioDraft}
                  onChange={(e) => setBioDraft(e.target.value)}
                  placeholder="60 characters. Say what you trade or how."
                  data-testid="trader-bio-input"
                  className="bg-zinc-950 border border-zinc-700 rounded-sm px-3 py-1.5 text-sm text-white w-full max-w-md focus:outline-none focus:border-[#00C805]"
                />
                <EmojiPicker
                  value={bioDraft}
                  onChange={(v) => setBioDraft(v.slice(0, 60))}
                  inputRef={bioInputRef}
                  testId="bio-emoji-picker"
                />
                <span className="mono text-[10px] text-zinc-600">{bioDraft.length}/60</span>
                <button
                  onClick={saveBio}
                  disabled={bioSaving}
                  data-testid="trader-bio-save"
                  className="text-[#00C805] hover:text-white p-1.5 border border-[#00C805]/50 rounded-sm"
                >
                  <Check size={12} weight="bold" />
                </button>
                <button
                  onClick={() => { setEditingBio(false); setBioDraft(profile.bio || ""); }}
                  className="text-zinc-500 hover:text-white p-1.5 border border-zinc-800 rounded-sm"
                >
                  <X size={12} />
                </button>
              </div>
            ) : profile.bio ? (
              <div className="mt-3 flex items-center gap-2 max-w-xl" data-testid="trader-bio">
                <p className="text-sm text-zinc-300 italic">"{profile.bio}"</p>
                {self && (
                  <button
                    onClick={() => setEditingBio(true)}
                    data-testid="trader-bio-edit-btn"
                    className="text-zinc-500 hover:text-white p-1"
                    title="Edit bio"
                  >
                    <PencilSimple size={12} />
                  </button>
                )}
              </div>
            ) : self ? (
              <button
                onClick={() => setEditingBio(true)}
                data-testid="trader-bio-add-btn"
                className="mt-3 mono text-[10px] text-zinc-500 hover:text-white uppercase tracking-widest border border-dashed border-zinc-800 hover:border-zinc-500 rounded-sm px-3 py-1.5 inline-flex items-center gap-2"
              >
                <PencilSimple size={11} /> Add a bio line
              </button>
            ) : null}
          </div>
          <div className="flex items-center gap-2">
            {user && !self && profile.num_bots > 0 && (
              <button
                onClick={toggleFollow}
                disabled={followBusy}
                data-testid="trader-follow-btn"
                className={`inline-flex items-center gap-1.5 text-sm rounded-sm px-3 py-2 mono text-[10px] uppercase tracking-widest transition-colors ${
                  following
                    ? "text-white border border-zinc-700 hover:border-zinc-500"
                    : "text-black bg-white hover:bg-zinc-200 border border-white"
                }`}
              >
                {following ? <UserMinus size={12} /> : <UserPlus size={12} weight="bold" />}
                {following ? "Following" : "Follow"}
              </button>
            )}
            <button
              onClick={shareProfile}
              data-testid="trader-share-btn"
              className="inline-flex items-center gap-1.5 text-sm text-zinc-300 hover:text-white border border-zinc-800 hover:border-zinc-500 rounded-sm px-3 py-2 mono text-[10px] uppercase tracking-widest"
            >
              <Share size={12} /> Share
            </button>
          </div>
        </div>

        {/* Summary stats */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <div className="p-4 border border-zinc-800 rounded-sm">
            <div className="mono text-[10px] text-zinc-500 uppercase tracking-widest">Lifetime realized</div>
            <div className={`mono text-3xl font-bold mt-1.5 ${positive ? "profit" : "loss"}`}>
              {fmtMoney(profile.total_realized_pl, { showSign: true })}
            </div>
          </div>
          <div className="p-4 border border-zinc-800 rounded-sm">
            <div className="mono text-[10px] text-zinc-500 uppercase tracking-widest">Public bots</div>
            <div className="mono text-3xl font-bold mt-1.5">{profile.num_bots}</div>
          </div>
          <div className="p-4 border border-zinc-800 rounded-sm">
            <div className="mono text-[10px] text-zinc-500 uppercase tracking-widest">Bot trades</div>
            <div className="mono text-3xl font-bold mt-1.5">{profile.num_trades}</div>
          </div>
        </div>

        {/* Badges */}
        {badges.length > 0 && (
          <div>
            <div className="mono text-[10px] text-zinc-500 uppercase tracking-widest mb-3 flex items-center gap-2">
              <Trophy size={12} className="text-[#00C805]" /> Badges · {badges.filter((b) => b.earned).length} earned
            </div>
            <BadgeGrid badges={badges} dataTestId="trader-badges" ownerTag={tag} />
          </div>
        )}

        {/* Bot table */}
        <div>
          <div className="mono text-[10px] text-zinc-500 uppercase tracking-widest mb-3">
            {profile.num_bots > 0 ? "All public bots" : "No bots yet"}
          </div>
          {profile.num_bots > 0 ? (
            <div className="border border-zinc-800 rounded-sm overflow-x-auto">
              <table className="w-full text-sm" data-testid="trader-bots-table">
                <thead className="text-[10px] mono uppercase tracking-widest text-zinc-500">
                  <tr className="border-b border-zinc-800">
                    <th className="text-left px-4 py-2.5">Symbol / strategy</th>
                    <th className="text-right px-3 py-2.5">Trades</th>
                    <th className="text-right px-3 py-2.5">Win rate</th>
                    <th className="text-right px-3 py-2.5">Realized P/L</th>
                    <th className="text-right px-4 py-2.5">Action</th>
                  </tr>
                </thead>
                <tbody className="mono">
                  {profile.bots.map((b) => (
                    <tr
                      key={b.bot_id}
                      className={`border-b border-zinc-800/50 hover:bg-zinc-800/30 ${
                        b._live ? "bg-[#00C805]/10 animate-pulse" : ""
                      }`}
                      data-testid={`trader-bot-row-${b.symbol}`}
                    >
                      <td className="px-4 py-3">
                        <Link to={`/b/${b.bot_id}`} className="hover:text-[#00C805]">
                          <div className="font-semibold text-base">{b.symbol}</div>
                          <div className="text-[10px] text-zinc-500 uppercase tracking-widest">
                            {STRATEGY_LABEL[b.strategy] || b.strategy}
                          </div>
                        </Link>
                      </td>
                      <td className="text-right px-3 py-3">
                        {b.num_trades}
                        <div className="text-[10px] text-zinc-500">{b.num_sells}S</div>
                      </td>
                      <td className={`text-right px-3 py-3 ${b.win_rate >= 50 ? "profit" : b.num_sells === 0 ? "text-zinc-600" : "loss"}`}>
                        {b.num_sells > 0 ? fmtPct(b.win_rate) : "—"}
                      </td>
                      <td className={`text-right px-3 py-3 text-base font-bold ${b.total_realized_pl >= 0 ? "profit" : "loss"}`}>
                        {b.num_sells > 0 ? fmtMoney(b.total_realized_pl, { showSign: true }) : <span className="text-zinc-600 text-sm">no data</span>}
                      </td>
                      <td className="text-right px-4 py-3">
                        <Link
                          to={`/b/${b.bot_id}`}
                          className="inline-flex items-center gap-1 text-xs text-white hover:text-[#00C805] border border-zinc-800 hover:border-[#00C805] rounded-sm px-2.5 py-1.5"
                        >
                          <Copy size={12} /> View
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="border border-zinc-800 rounded-sm p-10 text-center mono text-xs text-zinc-600">
              This trader hasn't deployed any bots yet. Check back later.
            </div>
          )}
        </div>

        {/* Footer CTA */}
        <div className="border border-zinc-800 rounded-sm p-6 flex items-center justify-between flex-wrap gap-4 bg-zinc-900/40">
          <div className="flex items-center gap-3">
            <Robot size={22} className="text-[#00C805]" />
            <div>
              <div className="font-semibold">Build your own trading bots</div>
              <div className="text-xs text-zinc-500 mt-1">Paper-trade with $100k, no real money at risk.</div>
            </div>
          </div>
          <Link
            to={user ? "/bots" : "/register"}
            className="inline-flex items-center gap-1.5 text-sm text-black bg-[#00C805] hover:bg-[#00b004] rounded-sm px-4 py-2 mono text-[10px] uppercase tracking-widest font-semibold"
          >
            {user ? "Deploy a bot" : "Start free →"}
          </Link>
        </div>
      </main>
    </div>
  );
}
